// See App.tsx's own comment on this same import -- required for the
// classic JSX transform this app's tsconfig uses.
import * as React from 'react';
import type { Dispatch, SetStateAction } from 'react';
import type { Locale } from '@accenturecodefoundry/mfe-pot-shared-i18n';
import type { ApplicationFormContentKey } from './content-client';
import type { FieldName, FormDraft } from './EiApplicationForm';
import { AddressField } from './AddressField';
import { formatIsoDate } from './format-date';

/** Mirrors citizen-context-fes's own status union. */
export type ProfileStatus = 'loading' | 'ready' | 'degraded';

/** Mirrors epayroll-fes's own EmploymentSummaryStatus union. */
export type EmploymentStatus = 'loading' | 'ready' | 'degraded';

/**
 * The insurable totals from `GET /api/v1/application/pre-fill`. Both null
 * whenever epayroll-ube was unreachable (the BFF always answers 200 -- see
 * its own route comment), which renders as the two rows being omitted
 * entirely rather than as an error.
 */
export interface InsurableTotals {
  insurableHours: number | null;
  insurableEarnings: number | null;
}

export interface EiPreFillConfirmStepProps {
  draft: FormDraft;
  setDraft: Dispatch<SetStateAction<FormDraft>>;
  label: (key: ApplicationFormContentKey) => string;
  fieldError: (field: FieldName) => string | undefined;
  locale: Locale;
  /**
   * The locale of the CMS content behind `label`, `undefined` until it has
   * loaded (usePageContents's loadedLocale). Keys the pickers -- see
   * loadedLocale's own comment.
   */
  contentLocale: Locale | undefined;
  profileStatus: ProfileStatus;
  employmentStatus: EmploymentStatus;
  /** True only when epayroll is 'ready' and reports currentStatus === 'separated'. */
  hasSeparation: boolean;
  totals: InsurableTotals;
  /** True while the address write-back to citizen-context is in flight. */
  patching: boolean;
  /** Set when that write-back failed; the wizard stays on this step. */
  patchError: string | null;
}

/**
 * Wizard step 1: "Confirm what we already know"
 * (docs/specs/012-ei-application-prefill.md, consolidated by spec 014).
 *
 * Since spec 014 this is the one screen for every personal and employment
 * field -- it absorbed the old Personal information, Employment separation,
 * Other employment and "Confirm your details" steps, plus education level.
 * Each field appears here exactly once:
 * - read-only rows for values a source supplies: name, phone and preferred
 *   language (citizen-context); employer, last day worked and the insurable
 *   totals (epayroll);
 * - inline inputs for everything with no source: date of birth, the
 *   address (pre-filled from citizen-context, and written back to it when
 *   the citizen continues -- see EiApplicationForm's goNext), education,
 *   job title, reason, pay rate and period, and other employment.
 *
 * Presentational only: every value, status and error is threaded in as a
 * prop, and field changes reach the draft through the form root's
 * delegated `data-field` listener (or AddressField's own), so the form owns
 * all data flow in one place.
 *
 * Each section degrades independently, per spec 012: a notice replaces the
 * sourced rows, and the fields they would have shown become inputs instead
 * (name/phone/language when the profile is degraded -- the removed
 * "Confirm your details" step's behaviour; employer/last day when epayroll
 * is degraded or the citizen isn't separated). The insurable rows have a
 * third state -- silently absent -- because a missing EPAYROLL_UBE_URL is a
 * deployment detail, not something to warn a citizen about.
 *
 * Visual treatment follows the Figma prototype's ReviewCluster/FieldRow
 * (EidetailScreen.tsx): flat bordered card per section, no shadow, a tinted
 * icon tile in each header (scds-icon, never lucide-react), label/value rows
 * on a fixed label column separated by hairlines; inputs sit inline among
 * the rows, as the prototype's own editable rows do.
 */
export function EiPreFillConfirmStep({
  draft,
  setDraft,
  label,
  fieldError,
  locale,
  contentLocale,
  profileStatus,
  employmentStatus,
  hasSeparation,
  totals,
  patching,
  patchError,
}: EiPreFillConfirmStepProps) {
  /**
   * Section header: tinted icon tile + title, per the prototype's
   * ReviewCluster header. Styling lives in EiApplicationForm.css.
   */
  function sectionHeader(iconName: string, titleKey: ApplicationFormContentKey) {
    return (
      <div className="ei-prefill__section-header">
        <span aria-hidden="true" className="ei-prefill__icon-tile">
          <scds-icon name={iconName} size="md" />
        </span>
        <span className="ei-prefill__section-title">{label(titleKey)}</span>
      </div>
    );
  }

  /**
   * Label/value row on a fixed label column (the prototype's `180px 1fr`
   * grid). The first row's top hairline is dropped by `:first-child` in
   * EiApplicationForm.css, so it sits flush against the header's border.
   */
  function row(labelKey: ApplicationFormContentKey, value: string) {
    return (
      <div className="ei-prefill__row">
        <span className="ei-prefill__row-label">{label(labelKey)}</span>
        <span className="ei-prefill__row-value">{value || '—'}</span>
      </div>
    );
  }

  /** An editable field sitting inline among the read-only rows. */
  function inputRow(content: React.ReactNode) {
    return <div className="ei-prefill__field">{content}</div>;
  }

  function textInput(field: FieldName, labelKey: ApplicationFormContentKey, value: string, type?: 'tel' | 'date') {
    return inputRow(
      <scds-text-input
        data-field={field}
        label={label(labelKey)}
        type={type}
        value={value}
        required
        error={fieldError(field)}
      />,
    );
  }

  /**
   * `key` is load-bearing: scds-picker reads its options' text once, so it
   * must remount when that text changes -- another field's options (bug
   * #186), or CMS labels arriving after first render or after a language
   * switch. See usePageContents's loadedLocale.
   */
  function picker(
    field: FieldName,
    labelKey: ApplicationFormContentKey,
    value: string,
    options: ReadonlyArray<{ value: string; labelKey: ApplicationFormContentKey }>,
    display?: 'select',
  ) {
    return inputRow(
      <scds-picker
        key={`${field}-${contentLocale ?? 'loading'}`}
        data-field={field}
        label={label(labelKey)}
        value={value}
        display={display}
        required
        error={fieldError(field)}
      >
        {options.map((option) => (
          <scds-picker-option key={option.value} value={option.value}>
            {label(option.labelKey)}
          </scds-picker-option>
        ))}
      </scds-picker>,
    );
  }

  const yesNoOptions = [
    { value: 'yes', labelKey: 'employment-insurance.application.yesOption' },
    { value: 'no', labelKey: 'employment-insurance.application.noOption' },
  ] as const;
  const languageOptions = [
    { value: 'en', labelKey: 'employment-insurance.application.languageEnglishOption' },
    { value: 'fr', labelKey: 'employment-insurance.application.languageFrenchOption' },
  ] as const;

  const preferredLanguageDisplay =
    draft.preferredLanguage === 'fr'
      ? label('employment-insurance.application.languageFrenchOption')
      : draft.preferredLanguage === 'en'
        ? label('employment-insurance.application.languageEnglishOption')
        : '';

  const fullName = [draft.firstName, draft.lastName].filter((part) => part.trim() !== '').join(' ');

  const lastDayWorkedDisplay = draft.lastDayWorked
    ? (formatIsoDate(draft.lastDayWorked, locale === 'fr' ? 'fr' : 'en') ?? draft.lastDayWorked)
    : '';

  // 'loading' deliberately falls through to the rows rather than blocking:
  // the draft is simply empty until the FES resolves, and each row's own
  // em-dash fallback reads better than a spinner that replaces the section.
  const profileDegraded = profileStatus === 'degraded';
  // Not-separated is treated the same as degraded per spec 012: there is no
  // last job on file, so the section explains itself and the citizen enters
  // the employer and last day worked instead. Only decided once epayroll is
  // 'ready' -- while it is 'loading' this section shows em-dash rows like
  // the profile section, so a separated citizen never sees a false warning.
  const employmentUnavailable =
    employmentStatus === 'degraded' || (employmentStatus === 'ready' && !hasSeparation);
  const numberLocale = locale === 'fr' ? 'fr-CA' : 'en-CA';

  return (
    <div className="ei-form__step">
      <p className="ei-form__hint">{label('employment-insurance.application.preFillIntro')}</p>

      <section className="ei-prefill__section">
        {sectionHeader('user', 'employment-insurance.application.preFillAboutYouHeading')}
        {profileDegraded ? (
          <div className="ei-prefill__notice-wrap">
            <scds-notice
              notice-title={label('employment-insurance.application.preFillProfileNoticeTitle')}
              tone="warning"
            >
              {label('employment-insurance.application.preFillProfileNoticeBody')}
            </scds-notice>
          </div>
        ) : null}
        <div className="ei-prefill__section-body">
          {profileDegraded ? (
            <>
              {textInput('firstName', 'employment-insurance.application.firstNameLabel', draft.firstName)}
              {textInput('lastName', 'employment-insurance.application.lastNameLabel', draft.lastName)}
            </>
          ) : (
            row('employment-insurance.application.preFillFullNameLabel', fullName)
          )}
          {textInput('dateOfBirth', 'employment-insurance.application.dateOfBirthLabel', draft.dateOfBirth, 'date')}
          {profileDegraded ? (
            <>
              {textInput('phone', 'employment-insurance.application.phoneLabel', draft.phone, 'tel')}
              {picker(
                'preferredLanguage',
                'employment-insurance.application.preferredLanguageLabel',
                draft.preferredLanguage,
                languageOptions,
              )}
            </>
          ) : (
            <>
              {row('employment-insurance.application.phoneLabel', draft.phone)}
              {row('employment-insurance.application.preferredLanguageLabel', preferredLanguageDisplay)}
            </>
          )}
          {inputRow(
            <AddressField draft={draft} setDraft={setDraft} label={label} fieldError={fieldError} disabled={patching} />,
          )}
          {patchError ? (
            <p role="alert" className="ei-form__error ei-prefill__field">
              {patchError}
            </p>
          ) : null}
          {picker(
            'educationLevel',
            'employment-insurance.application.educationLevelLabel',
            draft.educationLevel,
            [
              { value: 'high_school', labelKey: 'employment-insurance.application.educationHighSchoolOption' },
              { value: 'college_trade', labelKey: 'employment-insurance.application.educationCollegeTradeOption' },
              { value: 'undergraduate', labelKey: 'employment-insurance.application.educationUndergraduateOption' },
              { value: 'graduate', labelKey: 'employment-insurance.application.educationGraduateOption' },
              { value: 'other', labelKey: 'employment-insurance.application.otherOption' },
            ],
            'select',
          )}
        </div>
      </section>

      <section className="ei-prefill__section">
        {sectionHeader('briefcase', 'employment-insurance.application.preFillLastJobHeading')}
        {employmentUnavailable ? (
          <div className="ei-prefill__notice-wrap">
            <scds-notice
              notice-title={label('employment-insurance.application.preFillEmploymentNoticeTitle')}
              tone="warning"
            >
              {label('employment-insurance.application.preFillEmploymentNoticeBody')}
            </scds-notice>
          </div>
        ) : null}
        <div className="ei-prefill__section-body">
          {employmentUnavailable ? (
            <>
              {textInput('employerName', 'employment-insurance.application.employerNameLabel', draft.employerName)}
              {textInput(
                'lastDayWorked',
                'employment-insurance.application.lastDayWorkedLabel',
                draft.lastDayWorked,
                'date',
              )}
            </>
          ) : (
            <>
              {row('employment-insurance.application.employerNameLabel', draft.employerName)}
              {row('employment-insurance.application.lastDayWorkedLabel', lastDayWorkedDisplay)}
            </>
          )}
          {textInput('jobTitle', 'employment-insurance.application.jobTitleLabel', draft.jobTitle)}
          {picker('reasonCode', 'employment-insurance.application.reasonLabel', draft.reasonCode, [
            { value: 'shortage_of_work', labelKey: 'employment-insurance.application.reasonShortageOption' },
            { value: 'dismissal', labelKey: 'employment-insurance.application.reasonDismissalOption' },
            { value: 'quit', labelKey: 'employment-insurance.application.reasonQuitOption' },
            { value: 'other', labelKey: 'employment-insurance.application.otherOption' },
          ])}
          {inputRow(
            <scds-currency-input
              data-field="payRate"
              label={label('employment-insurance.application.payRateLabel')}
              value={draft.payRate ?? undefined}
              required
              error={fieldError('payRate')}
            />,
          )}
          {picker(
            'payPeriod',
            'employment-insurance.application.payPeriodLabel',
            draft.payPeriod,
            [
              { value: 'hourly', labelKey: 'employment-insurance.application.payPeriodHourlyOption' },
              { value: 'weekly', labelKey: 'employment-insurance.application.payPeriodWeeklyOption' },
              { value: 'biweekly', labelKey: 'employment-insurance.application.payPeriodBiweeklyOption' },
              { value: 'monthly', labelKey: 'employment-insurance.application.payPeriodMonthlyOption' },
            ],
            'select',
          )}
          {!employmentUnavailable && totals.insurableHours !== null
            ? row(
                'employment-insurance.application.preFillInsurableHoursLabel',
                `${new Intl.NumberFormat(numberLocale).format(totals.insurableHours)} ${label(
                  'employment-insurance.application.preFillHoursUnit',
                )}`,
              )
            : null}
          {!employmentUnavailable && totals.insurableEarnings !== null
            ? row(
                'employment-insurance.application.preFillInsurableEarningsLabel',
                new Intl.NumberFormat(numberLocale, { style: 'currency', currency: 'CAD' }).format(
                  totals.insurableEarnings,
                ),
              )
            : null}
          {picker(
            'hadOtherEmployers',
            'employment-insurance.application.hadOtherEmployersLabel',
            draft.hadOtherEmployers,
            yesNoOptions,
          )}
          {draft.hadOtherEmployers === 'yes'
            ? textInput(
                'otherEmployerName',
                'employment-insurance.application.otherEmployerNameLabel',
                draft.otherEmployerName,
              )
            : null}
        </div>
      </section>
    </div>
  );
}
